import _, {difference, isEqual} from 'lodash';

import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {TrueAvailabilityStatusesEnum} from '../../../../helpers/models/schedulingLinkAvailabilityHelpers';
import {isSchedulingLinkOutdated} from '../../../../helpers/models/schedulingLinkHelpers';
import {
  DraftSchedulingLinkModel,
  SchedulingLinkAvailabilityTypesEnum,
  SchedulingLinkStandardAvailabilityBlockModel,
} from '../../../../models/api/schedulingLinkModel';
import {AppAction} from '../../../appActions';
import {CalendarsPanelActionsEnum} from '../calendarsPanelActions';
import {CalendarsPanelSchedulingActionsEnum} from './calendarsPanelSchedulingActions';
import {CalendarsPanelSchedulingState} from './calendarsPanelSchedulingState';

/*
 * Calendar scheduling panel.
 */

export function calendarsPanelSchedulingReducer(
  state: CalendarsPanelSchedulingState | undefined,
  action: AppAction,
): CalendarsPanelSchedulingState | undefined {
  if (!state) {
    return state;
  }

  switch (action.type) {
    case CalendarsPanelSchedulingActionsEnum.UPDATE_TRUE_AVAILABILITY_STATUS:
      return {
        ...state,
        trueAvailabilityStatus: action.payload.status,
      };

    case CalendarsPanelSchedulingActionsEnum.UPDATE_ORGANIZER_ADDRESS:
      return {
        ...state,
        organizerAddress: action.payload.organizerAddress,
      };

    case CalendarsPanelSchedulingActionsEnum.UPDATE_DRAFT_LINK:
      return {
        ...state,
        draftLink: {
          ...state.draftLink,
          ...action.payload.updatedLink,
        },
        didEditDraft: true,
      };

    case CalendarsPanelSchedulingActionsEnum.UPDATE_MEETING_PROPERTIES:
      return {
        ...state,
        draftLink: {
          ...state.draftLink,
          meetingProperties: {
            ...state.draftLink.meetingProperties,
            ...action.payload.updatedProperties,
          },
        },
        didEditDraft: true,
      };

    case CalendarsPanelSchedulingActionsEnum.UPDATE_MEETING_DURATION:
      return {
        ...state,
        draftLink: makeDraftLinkWithMeetingDuration(state.draftLink, action.payload.durationMinutes),
        didEditDraft: true,
      };

    case CalendarsPanelSchedulingActionsEnum.ADD_ADDITIONAL_GUESTS: {
      const draftLink = makeDraftLinkWithNewGuests(state.draftLink, action.payload.newGuests);
      if (draftLink === state.draftLink) {
        return state;
      }

      return {
        ...state,
        draftLink,
        didEditDraft: true,
      };
    }

    case CalendarsPanelSchedulingActionsEnum.FETCH_SCHEDULING_LINK: {
      if (state.status === AsyncStatusesEnum.LOADED) {
        return state;
      }

      return {
        ...state,
        status: AsyncStatusesEnum.LOADING,
      };
    }

    case CalendarsPanelSchedulingActionsEnum.FETCH_SCHEDULING_LINK_COMPLETED: {
      return {
        ...state,
        status: AsyncStatusesEnum.LOADED,
        draftLink: action.payload.draftLink,
        trueAvailabilityStatus: isSchedulingLinkOutdated(action.payload.draftLink)
          ? TrueAvailabilityStatusesEnum.PAST_AVAILABILITY
          : state.trueAvailabilityStatus,
      };
    }

    case CalendarsPanelSchedulingActionsEnum.FETCH_SCHEDULING_LINK_FAILED: {
      if (state.status === AsyncStatusesEnum.LOADED) {
        return state;
      }

      return {
        ...state,
        status: AsyncStatusesEnum.FAILED,
      };
    }

    case CalendarsPanelActionsEnum.ADD_CALENDAR_ADDRESS: {
      // Don't add the organizer address as an additional guest.
      if (action.payload.address === state.organizerAddress) {
        return state;
      }

      // Don't add non primary calendars as additional guests.
      if (!action.payload.isPrimary) {
        return state;
      }

      // Don't add guests that already exist.
      if (state.draftLink.meetingProperties.additionalGuests.includes(action.payload.address)) {
        return state;
      }

      return {
        ...state,
        draftLink: makeDraftLinkWithNewGuests(state.draftLink, [action.payload.address]),
        didEditDraft: true,
      };
    }

    case CalendarsPanelActionsEnum.UPDATE_VISIBLE_CALENDARS: {
      const doubleBookingCalendarVersions = _(action.payload.viewingNowCalendars)
        .map((viewingNow) => viewingNow.calendarVersionId)
        .compact()
        .concat(action.payload.selectedCalendarVersionIds)
        .uniq()
        .value();

      if (isEqual(doubleBookingCalendarVersions, state.draftLink.doubleBookingCalendarVersions)) {
        return state;
      }

      return {
        ...state,
        draftLink: {
          ...state.draftLink,
          doubleBookingCalendarVersions,
        },
        didEditDraft: true,
      };
    }

    case CalendarsPanelActionsEnum.NAVIGATE_CALENDARS: {
      const doubleBookingCalendarVersions = [
        ...action.payload.parameters.selectedCalendarVersionIds,
        ...action.payload.parameters.viewingNowCalendarVersionIds,
      ];

      if (isEqual(doubleBookingCalendarVersions, state.draftLink.doubleBookingCalendarVersions)) {
        return state;
      }

      return {
        ...state,
        draftLink: {
          ...state.draftLink,
          doubleBookingCalendarVersions,
        },
        didEditDraft: true,
      };
    }

    default:
      return state;
  }
}

/*
 * Helpers.
 */

function makeDraftLinkWithMeetingDuration(
  draftLink: DraftSchedulingLinkModel,
  durationMinutes: number,
): DraftSchedulingLinkModel {
  if (draftLink.availability.type !== SchedulingLinkAvailabilityTypesEnum.STANDARD) {
    return draftLink;
  }

  if (draftLink.meetingProperties.durationMinutes === durationMinutes) {
    return draftLink;
  }

  return {
    ...draftLink,
    meetingProperties: {
      ...draftLink.meetingProperties,
      durationMinutes,
    },
    availability: {
      type: SchedulingLinkAvailabilityTypesEnum.STANDARD,
      blocks: draftLink.availability.blocks.map((slot) => expandSlotToDuration(slot, durationMinutes)),
    },
  };
}

function expandSlotToDuration(
  slot: SchedulingLinkStandardAvailabilityBlockModel,
  durationMinutes: number,
): SchedulingLinkStandardAvailabilityBlockModel {
  return {
    ...slot,
    end: Math.max(slot.start + durationMinutes * 60 * 1000, slot.end),
  };
}

function makeDraftLinkWithNewGuests(draftLink: DraftSchedulingLinkModel, newGuests: ReadonlyArray<string>) {
  const guestsToAdd = difference(newGuests, draftLink.meetingProperties.additionalGuests);
  if (guestsToAdd.length === 0) {
    return draftLink;
  }

  return {
    ...draftLink,
    meetingProperties: {
      ...draftLink.meetingProperties,
      additionalGuests: [...draftLink.meetingProperties.additionalGuests, ...guestsToAdd],
    },
  };
}
